import { Component, inject } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import {
    FormsModule,
    ReactiveFormsModule,
    FormBuilder,
    FormGroup,
    Validators,
    FormControl,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { map, startWith } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';
import { TranslocoModule } from '@ngneat/transloco';
import { ApiService } from '../../services/api/api.service';
import { FileUploadModule, FileUploadValidators } from '@iplab/ngx-file-upload';
import { StateResponse } from '../../interfaces/StateResponse';
import { State } from '../../interfaces/State';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import * as CVSS from '../../../assets/scripts/cvsscalc31.js';
import { VulnerabilityAssessmentResponse } from '../../interfaces/VulnerabilityAssessmentResponse';
import { VulnerabilityAssessment } from '../../interfaces/VulnerabilityAssessment';
import { Asset } from '../../interfaces/Asset';
import { Observable } from 'rxjs';
import { AssetResponse } from '../../interfaces/AssetResponse';
import { VulnerabilityAssessmentResult } from '../../interfaces/VulnerabilityAssessmentResult';
import { MatDialog } from '@angular/material/dialog';
import { AlertDialogComponent } from '../../common/alert-dialog/alert-dialog.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { GlobalConstants } from '../../common/global-constants';

@Component({
    selector: 'app-v-create-vulnerability',
    standalone: true,
    imports: [
        MatTableModule,
        MatCardModule,
        MatMenuModule,
        MatButtonModule,
        RouterLink,
        FormsModule,
        FileUploadModule,
        MatFormFieldModule,
        MatInputModule,
        MatCheckboxModule,
        MatSelectModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatButtonToggleModule,
        MatIconModule,
        MatAutocompleteModule,
        MatProgressSpinnerModule,
        ReactiveFormsModule,
        AsyncPipe,
        TranslocoModule,
        NgFor,
        NgIf,
    ],
    templateUrl: './v-create-vulnerability.component.html',
    styleUrl: './v-create-vulnerability.component.scss',
})
export class VCreateVulnerabilityComponent {
    // vulnerabilityAssessmentId
    vulnerabilityAssessmentId: string;

    // vulnerabilityAssessment
    vulnerabilityAssessment: VulnerabilityAssessment;

    // isToggled
    isToggled = false;

    // Forms
    addVulnerabilityForm: FormGroup;

    // isLoadingSubmit
    isLoadingSubmit = false;

    // Array of states
    states: State[] = [];

    selectedState: State | undefined;

    // Array of assets
    assets: Asset[] = [];
    selectedAsset: Asset | undefined;
    assetControl = new FormControl<string | Asset>('', [Validators.required]);
    filteredOptions!: Observable<Asset[]>;

    // Score
    score: number = 0.0;

    // Common Vulnerability Scoring System Version 3.1 Calculator
    attackVector?: string = 'N';
    attackComplexity?: string = 'L';
    privilegesRequired?: string = 'N';
    userInteraction?: string = 'N';
    scope?: string = 'U';
    confidentiality?: string = 'N';
    integrity?: string = 'N';
    availability?: string = 'N';

    // Show alert
    showAlert: boolean = false;

    // Error message
    errorMsg: string;

    readonly dialog = inject(MatDialog);

    constructor(
        private fb: FormBuilder,
        public themeService: CustomizerSettingsService,
        private apiService: ApiService,
        private router: Router,
        private route: ActivatedRoute
    ) {
        this.addVulnerabilityForm = this.fb.group({
            name: ['', [Validators.required, Validators.maxLength(200)]],
            referenceCode: ['', [Validators.maxLength(6)]],
            expectedResolutionDate: [{ value: '', disabled: true }, []],
            description: ['', []],
            port: ['', [Validators.min(1), Validators.max(65535)]],
            resolutionDate: [{ value: '', disabled: true }, []],
            impact: ['', []],
            protocol: ['', [Validators.maxLength(30)]],
            state: [this.selectedState, [Validators.required]],
            recommendation: ['', []],
            vulnerabilityDate: [
                { value: '', disabled: true },
                [Validators.required],
            ],
            score: [{ value: this.score, disabled: true }, []],
            asset: this.assetControl,
            cvssClassification: ['', [Validators.maxLength(80)]],
            file: [
                null,
                [
                    FileUploadValidators.filesLimit(1),
                    FileUploadValidators.accept(['.zip']),
                ],
            ],
            attackVector: [this.attackVector, []],
            attackComplexity: [this.attackComplexity, []],
            privilegesRequired: [this.privilegesRequired, []],
            userInteraction: [this.userInteraction, []],
            scope: [this.scope, []],
            confidentiality: [this.confidentiality, []],
            integrity: [this.integrity, []],
            availability: [this.availability, []],
        });
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for role name
     */
    get roleName(): string {
        return localStorage.getItem('roleName') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    ngOnInit() {
        this.route.params.subscribe((params) => {
            this.vulnerabilityAssessmentId =
                params['vulnerabilityAssessmentId'];

            this.apiService.getState().subscribe((response: StateResponse) => {
                this.states.push(...response.states);
            });

            this.apiService
                .getVulnerabilityAssessmentById(
                    this.vulnerabilityAssessmentId,
                    this.roleName == GlobalConstants.internal
                )
                .subscribe((data: VulnerabilityAssessmentResponse) => {
                    this.vulnerabilityAssessment = data.vulnerabilityAssessment;
                    console.log(this.vulnerabilityAssessment);
                    if (this.vulnerabilityAssessment != undefined) {
                    }
                });

            this.filteredOptions = this.assetControl.valueChanges.pipe(
                startWith(''),
                map((value) => {
                    const name =
                        typeof value === 'string' ? value : value?.name;
                    return name
                        ? this._filter(name as string)
                        : this.assets.slice();
                })
            );

            this.onValChange('init', '0.0');
        });
    }

    // RTL Mode
    toggleRTLEnabledTheme() {
        this.themeService.toggleRTLEnabledTheme();
    }

    // onKeydown
    onKeydown(event: Event) {
        //console.log(event);
        return false;
    }

    // Popup Trigger
    classApplied = false;
    toggleClass() {
        this.classApplied = !this.classApplied;
    }

    /**
     * isInternal
     */
    get isInternal(): boolean {
        return (localStorage.getItem('roleName') ?? '') != GlobalConstants.internal
            ? false
            : true;
    }

    private _filter(value: string): Asset[] {
        const filterValue = value.toLowerCase();
        if (filterValue.length < 3) {
            this.assets = [];
            return this.assets;
        } else {
            if (this.isInternal) {
                this.apiService
                    .getAssetsByBranchOficceId(
                        this.vulnerabilityAssessment.branchOffice.id,
                        filterValue
                    )
                    .subscribe((response: AssetResponse) => {
                        if (response && response.assets) {
                            this.assets = [];
                            this.assets.push(...response.assets);
                        }
                    });
            } else {
                this.apiService
                    .getAssetsByPattern(filterValue)
                    .subscribe((response: AssetResponse) => {
                        if (response && response.assets) {
                            this.assets = [];
                            this.assets.push(...response.assets);
                        }
                    });
            }

            return this.assets.filter((option) =>
                option.name.toLowerCase().includes(filterValue)
            );
        }
    }

    // displayFn
    displayFn(asset: Asset): string {
        return asset && asset.name ? asset.name : '';
    }

    // onValChange
    onValChange(name: string, value: string) {
        switch (name) {
            case 'attackVector':
                this.attackVector = value;
                break;
            case 'attackComplexity':
                this.attackComplexity = value;
                break;
            case 'privilegesRequired':
                this.privilegesRequired = value;
                break;
            case 'userInteraction':
                this.userInteraction = value;
                break;
            case 'scope':
                this.scope = value;
                break;
            case 'confidentiality':
                this.confidentiality = value;
                break;
            case 'integrity':
                this.integrity = value;
                break;
            case 'availability':
                this.availability = value;
                break;
        }
        var output = CVSS.CVSS31.calculateCVSSFromMetrics(
            this.attackVector,
            this.attackComplexity,
            this.privilegesRequired,
            this.userInteraction,
            this.scope,
            this.confidentiality,
            this.integrity,
            this.availability
        );

        var result;
        if (output.success === true) {
            this.addVulnerabilityForm.patchValue({
                score: output.baseMetricScore
                    ? parseFloat(output.baseMetricScore)
                    : 0.0,
                cvssClassification: output.vectorString,
            });
            result =
                'Base score is ' +
                output.baseMetricScore +
                '. ' +
                'Base severity is ' +
                output.baseSeverity +
                '. ' +
                'Temporal score is ' +
                output.temporalMetricScore +
                '. ' +
                'Temporal severity is ' +
                output.temporalSeverity +
                '. ' +
                'Environmental score is ' +
                output.environmentalMetricScore +
                '. ' +
                'Environmental severity is ' +
                output.environmentalSeverity +
                '. ' +
                'Vector string is ' +
                output.vectorString +
                '. ';
        } else {
            result =
                "An error occurred. The error type is '" +
                output.errorType +
                "' and the metrics with errors are " +
                output.errorMetrics +
                '.';
        }
        console.log(result);
    }

    /**
     * On submit
     */
    onSubmit(): void {
        // Return if the form is invalid
        if (this.addVulnerabilityForm.invalid || this.classApplied) {
            this.showAlert = false;
            return;
        }

        // Disable the form
        this.addVulnerabilityForm.disable();

        // Show the spinner
        this.isLoadingSubmit = true;

        // Hide the alert
        this.showAlert = false;

        // Add Vulnerability
        const vulnerabilityAssessmentResult: VulnerabilityAssessmentResult = {
            name: this.addVulnerabilityForm.get('name')?.value,
            score: this.addVulnerabilityForm.get('score')?.value,
            description: this.addVulnerabilityForm.get('description')?.value,
            impact: this.addVulnerabilityForm.get('impact')?.value,
            recommendation:
                this.addVulnerabilityForm.get('recommendation')?.value,
            referenceCode:
                this.addVulnerabilityForm.get('referenceCode')?.value,
            port: this.addVulnerabilityForm.get('port')?.value,
            protocol: this.addVulnerabilityForm.get('protocol')?.value,
            cvssClassification:
                this.addVulnerabilityForm.get('cvssClassification')?.value,
            vulnerabilityDate: this.addVulnerabilityForm.get(
                'vulnerabilityDate'
            )?.value
                ? this.addVulnerabilityForm
                      .get('vulnerabilityDate')
                      ?.value.getTime()
                : '',
            expectedResolutionDate: this.addVulnerabilityForm.get(
                'expectedResolutionDate'
            )?.value
                ? this.addVulnerabilityForm
                      .get('expectedResolutionDate')
                      ?.value.getTime()
                : '',
            resolutionDate: this.addVulnerabilityForm.get('resolutionDate')
                ?.value
                ? this.addVulnerabilityForm
                      .get('resolutionDate')
                      ?.value.getTime()
                : '',
            state: this.addVulnerabilityForm.get('state')?.value,
            vulnerabilityAssessment: this.vulnerabilityAssessment,
            asset: this.addVulnerabilityForm.get('asset')?.value,
        };

        const formData = new FormData();

        // Append JSON data as a blob to maintain proper JSON format
        const jsonBlob = new Blob(
            [JSON.stringify(vulnerabilityAssessmentResult)],
            {
                type: 'application/json',
            }
        );

        formData.append('vulnerabilityAssessmentResult', jsonBlob);

        if (this.addVulnerabilityForm.get('file')?.value) {
            formData.append(
                'file',
                this.addVulnerabilityForm.get('file')?.value[0]
            );
        }

        this.apiService.addVulnerabilityAssessmentResult(formData).subscribe({
            next: (value) => {
                // Re-enable the form
                this.addVulnerabilityForm.enable();

                // Show the alert
                this.showAlert = true;

                // Hide the spinner
                this.isLoadingSubmit = false;
            },
            error: (error) => {
                let errMsg: string = error;
                const dialogRef = this.dialog.open(AlertDialogComponent, {
                    data: {
                        title: 'ERROR',
                        message: errMsg,
                    },
                });
            },
            complete: () => {
                this.router.navigateByUrl(
                    `/vulnerability/view-vulnerabilities/${this.vulnerabilityAssessmentId}`
                );
            },
        });
    }
}
